import { useEffect, useState, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Mail, Phone, ExternalLink, Edit3, Plus, Trash2, Upload, UserX, UserCheck, Building2 } from "lucide-react";

const ROLE_COLORS = {
  admin: "#B2674A",
  pastoral: "#C69E5F",
  ministry_support: "#AEBDB1",
};

// Absolute URL for photos uploaded via our API (they come back as relative "/api/users/.../photo?v=...")
function resolvePicture(pic) {
  if (!pic) return null;
  if (pic.startsWith("http://") || pic.startsWith("https://")) return pic;
  if (pic.startsWith("/api/")) {
    const origin = API_BASE.replace(/\/api$/, "");
    return `${origin}${pic}`;
  }
  return pic;
}

const emptyForm = {
  user_id: null,
  name: "", title: "", email: "", phone: "",
  department: "", role: "ministry_support",
  picture: "", active: true,
};

export default function StaffDirectory() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const [u, l] = await Promise.all([
      api.get(`/users${includeInactive ? "?include_inactive=true" : ""}`),
      api.get("/quick-links"),
    ]);
    setUsers(u.data);
    setQuickLinks(l.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const filtered = users.filter((u) =>
    !q ||
    [u.name, u.email, u.title, u.department].some((f) =>
      (f || "").toLowerCase().includes(q.toLowerCase())
    )
  );

  const openEdit = (u) => {
    setIsNew(false);
    setForm({
      user_id: u.user_id,
      name: u.name || "",
      title: u.title || "",
      email: u.email || "",
      phone: u.phone || "",
      department: u.department || "",
      role: u.role,
      picture: u.picture || "",
      active: u.active !== false,
    });
    setOpen(true);
  };

  const openNew = () => {
    setIsNew(true);
    setForm({ ...emptyForm });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isNew && !form.email.trim()) return toast.error("Email is required for a new staff member");
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/users", {
          email: form.email.trim().toLowerCase(),
          name: form.name,
          role: form.role,
          title: form.title || null,
          phone: form.phone || null,
          department: form.department || null,
          picture: form.picture || null,
        });
        toast.success("Staff member added");
      } else {
        // Profile fields
        await api.patch(`/users/${form.user_id}`, {
          name: form.name,
          title: form.title || null,
          phone: form.phone || null,
          department: form.department || null,
          picture: form.picture || null,
          active: form.active,
        });
        // Role (separate endpoint)
        const original = users.find((x) => x.user_id === form.user_id);
        if (original && original.role !== form.role) {
          await api.patch(`/users/${form.user_id}/role`, { role: form.role });
        }
        toast.success("Staff profile updated");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  const removeStaff = async () => {
    if (!form.user_id) return;
    if (!confirm(`Permanently remove ${form.name}? This cannot be undone. (Tip: you can deactivate instead.)`)) return;
    try {
      await api.delete(`/users/${form.user_id}`);
      toast.success("Staff removed");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to remove");
    }
  };

  const uploadPhoto = async (file) => {
    if (!form.user_id) {
      toast.error("Save the new staff member first, then upload a photo on edit.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/users/${form.user_id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, picture: data.picture }));
      toast.success("Photo updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to upload photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="staff-directory">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl text-brand-primary">Staff &amp; Quick Links</h2>
          <p className="text-brand-primary/70 mt-1">People, roles, and the tools you reach for every day.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-primary/50" />
            <Input
              data-testid="staff-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search staff…"
              className="pl-10 h-11 w-80 rounded-full bg-white border-brand-primary/10"
            />
          </div>
          {isAdmin && (
            <>
              <label className="flex items-center gap-2 text-sm text-brand-primary/70 px-3 py-2 rounded-full border border-brand-primary/15 bg-white">
                Show inactive
                <Switch
                  data-testid="staff-include-inactive"
                  checked={includeInactive}
                  onCheckedChange={setIncludeInactive}
                />
              </label>
              <Button
                data-testid="add-staff-button"
                onClick={openNew}
                className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-11 px-5 shadow-warm"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add staff
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Staff grid */}
      <section>
        <h3 className="font-heading text-xl text-brand-primary mb-4">Team members ({filtered.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="staff-grid">
          {filtered.length === 0 && (
            <Card className="md:col-span-2 lg:col-span-3 rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 text-brand-primary/60">
              No staff match your search.
            </Card>
          )}
          {filtered.map((u) => (
            <Card
              key={u.user_id}
              data-testid={`staff-card-${u.user_id}`}
              className={`rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg ${
                u.active === false ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-14 w-14 rounded-full bg-cream grid place-items-center overflow-hidden border border-brand-primary/10 shrink-0">
                    {u.picture ? (
                      <img src={resolvePicture(u.picture)} alt={u.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="font-heading text-xl text-terracotta">{u.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-lg font-medium text-brand-primary truncate">{u.name}</p>
                    <p className="text-sm text-brand-primary/65 truncate">{u.title || ROLE_LABELS[u.role]}</p>
                  </div>
                </div>
                {isAdmin && (
                  <button
                    data-testid={`edit-staff-${u.user_id}`}
                    onClick={() => openEdit(u)}
                    className="text-brand-primary/40 hover:text-terracotta p-1 transition shrink-0"
                    aria-label={`Edit ${u.name}`}
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Badge className="rounded-full border-0" style={{ background: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role] }}>
                  {ROLE_LABELS[u.role]}
                </Badge>
                {u.department && (
                  <Badge variant="outline" className="rounded-full border-brand-primary/15 text-brand-primary/70 text-[10px] uppercase tracking-wider">
                    <Building2 className="h-3 w-3 mr-1" /> {u.department}
                  </Badge>
                )}
                {u.active === false && (
                  <Badge className="rounded-full bg-brand-primary/10 text-brand-primary/70 border-0 text-[10px] uppercase tracking-wider">
                    <UserX className="h-3 w-3 mr-1" /> Inactive
                  </Badge>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-brand-primary/10 space-y-1.5 text-sm">
                <a href={`mailto:${u.email}`} className="flex items-center gap-2 text-brand-primary/75 hover:text-terracotta truncate">
                  <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{u.email}</span>
                </a>
                {u.phone && (
                  <a href={`tel:${u.phone}`} className="flex items-center gap-2 text-brand-primary/75 hover:text-terracotta">
                    <Phone className="h-3.5 w-3.5 shrink-0" /> {u.phone}
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Quick links */}
      <section>
        <h3 className="font-heading text-xl text-brand-primary mb-4">Quick links</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="staff-quick-links">
          {quickLinks.map((ql) => (
            <a
              key={ql.id}
              href={ql.url}
              target="_blank"
              rel="noreferrer"
              className="group rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg"
            >
              <div className="flex items-center justify-between">
                <p className="font-medium text-brand-primary">{ql.title}</p>
                <ExternalLink className="h-4 w-4 text-terracotta opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="text-xs text-brand-primary/55 mt-1 truncate">
                {ql.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </p>
              {ql.category && (
                <Badge className="mt-3 rounded-full bg-sage/20 text-brand-primary border-0 text-[10px] uppercase tracking-wider">
                  {ql.category}
                </Badge>
              )}
            </a>
          ))}
        </div>
      </section>

      {/* Edit / Add dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setIsNew(false); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {isNew ? "Add staff member" : `Edit ${form.name || "staff"}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Photo */}
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-cream grid place-items-center overflow-hidden border border-brand-primary/10 shrink-0">
                {form.picture
                  ? <img src={resolvePicture(form.picture)} alt="" className="h-full w-full object-cover" />
                  : <span className="font-heading text-xl text-terracotta">{form.name?.[0]?.toUpperCase() || "?"}</span>
                }
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-brand-primary/60">Profile photo</p>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="staff-photo-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadPhoto(f);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isNew || uploading}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="staff-photo-upload-btn"
                    className="rounded-full border-brand-primary/15"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {uploading ? "Uploading…" : (form.picture ? "Replace" : "Upload")}
                  </Button>
                  {form.picture && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm({ ...form, picture: "" })}
                      data-testid="staff-photo-clear-btn"
                      className="text-brand-primary/60 hover:text-terracotta"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {isNew && <p className="text-xs text-brand-primary/50 mt-1">Save the new staff member first to enable photo upload.</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">Full name</label>
                <Input
                  data-testid="staff-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jonathan Glover"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">Title / role</label>
                <Input
                  data-testid="staff-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Operations Pastor, Lead Pastor, Worship Director"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">
                  Email {isNew ? "" : "(tied to Google login — not editable)"}
                </label>
                <Input
                  data-testid="staff-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!isNew}
                  placeholder="name@familychapel.org"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">Phone</label>
                <Input
                  data-testid="staff-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">Department / ministry</label>
                <Input
                  data-testid="staff-department-input"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="e.g. Worship, Youth, Operations"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">Role (permissions)</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isNew && (
                <div className="col-span-2 flex items-center justify-between pt-2 border-t border-brand-primary/10">
                  <div>
                    <p className="text-sm text-brand-primary">Active</p>
                    <p className="text-xs text-brand-primary/55">Inactive staff are hidden from the directory and can't sign in.</p>
                  </div>
                  <Switch
                    data-testid="staff-active-switch"
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-between gap-2 sm:justify-between">
            {!isNew ? (
              <Button
                type="button"
                variant="ghost"
                onClick={removeStaff}
                data-testid="staff-delete-button"
                className="text-terracotta hover:bg-terracotta/5 rounded-full"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-full border-brand-primary/15"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="staff-save-button"
                className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
              >
                <UserCheck className="h-4 w-4 mr-1.5" />
                {saving ? "Saving…" : (isNew ? "Add staff" : "Save changes")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
